import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  ConsentTypes,
  identificationFormRules,
  sharedFormValidator,
} from '@credit-acceptance/shareable-utilities';
import { adobeAnalytics } from 'adobe-analytics';
import isEqual from 'lodash/isEqual';
import {
  catchError,
  EMPTY,
  filter,
  map,
  Observable,
  startWith,
  takeUntil,
  tap,
} from 'rxjs';

import { error_sign, PREQUAL_JSON } from '../../assets/constants';
import { ConsentTextInfoRestService } from '../services/consent-test-info-rest.service';
import { PrequalService } from '../services/prequal.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { accessToken, cacChubSSNId } from '../shared/state/local-cache';
import { applyServerFieldErrorsToGroup } from '../summary/caps-validation-field-errors';
import { SiblingValidationService } from '../validators/sibling-validation.service';

type SsnFormFieldErrorLabels = {
  errors: {
    ide1: string;
    ide2: string;
    ide3: string;
  };
};

@Component({
  selector: 'capq-identification',
  templateUrl: './identification.component.html',
  styleUrls: ['./identification.component.scss'],
  standalone: false,
})
export class IdentificationComponent extends Unsubscribe implements OnInit {
  @Input() adobeScriptLoaded = false;
  @Output() activeStep = new EventEmitter<number>();
  @Output() setConsent = new EventEmitter<boolean>();
  initState = {
    ssn: ['', this.fieldValidator()],
    agreement_consent: [false, this.fieldValidator()],
  };
  identificationForm = this.formBuilder.group(this.initState);
  isWaiting = false;
  uuid: string | Blob | undefined;
  consentAgreedFlag = false;
  consentCreditAuth = '';
  consentCreditAuthVersion = 0;

  firstName: string | undefined;
  ssnInvalidErrMessage = false;
  personalData: any;
  exclamation = error_sign;
  globalText: any;
  formFieldData!: SsnFormFieldErrorLabels;
  formFieldErrorsMap: Record<string, string> = {};
  formIsIncomplete$!: Observable<boolean>;
  consentversionid = '';
  residenceData: any;
  showFormErrorMessages = false;
  errorMessage: any;
  pageViewFired = false;
  formStartEventEmitted = false;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly prequalService: PrequalService,
    private siblingValidationService: SiblingValidationService,
    private cdRef: ChangeDetectorRef,
    private consentTextInfoRestService: ConsentTextInfoRestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.identificationForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.adobeScriptLoaded && !this.pageViewFired) {
          adobeAnalytics.emitEvent('cac-page-view', 'identification', {
            form_design: 'v 1.0',
            section_name: 'identification',
            form_submission_id: '',
            dealer_list: '',
            form_submission_status: '',
            event_type: 'cac-page-view',
          });
          this.pageViewFired = true;
        }
        if (
          !isEqual(this.initState, this.identificationForm.getRawValue()) &&
          !this.formStartEventEmitted &&
          this.adobeScriptLoaded
        ) {
          this.formStartEventEmitted = true;
          adobeAnalytics.emitFormStart('identification');
        }
      });
    this.siblingValidationService.validate$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.showFormErrorMessages = true;
        this.identificationForm.controls.ssn.markAsTouched();
        this.fieldValidator()(this.identificationForm.controls.ssn);
        this.cdRef.detectChanges();
        this.siblingValidationService.reportValidation(
          5,
          this.identificationForm.getRawValue(),
          this.identificationForm.valid
        );
      });

    this.prequalService.ssnInvalidErrorMessage$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        if (data) {
          this.ssnInvalidErrMessage = true;
        }
      });

    this.prequalService.serverFieldErrors$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((errors) => {
        applyServerFieldErrorsToGroup(errors, 'identification', (name) =>
          this.identificationForm.get(name)
        );
        this.cdRef.detectChanges();
      });

    this.initForm();

    this.consentTextInfoRestService
      .getConsentByType(ConsentTypes.OSM_CREDIT_AUTH)
      .pipe(
        tap(({ headers }) => {
          const token = headers?.get('access-token');
          if (token) accessToken(token);

          const ssid = headers?.get('cac-chub-ssid');
          if (ssid) cacChubSSNId(ssid);
        }),
        map(({ body }) => body?.getConsentByType),
        filter(Boolean),
        catchError((error) => {
          console.error('Failed to load consent text:', error);
          return EMPTY;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ consenttext, consenttextid, consentversion }) => {
        this.consentCreditAuth = consenttext;
        this.consentCreditAuthVersion = consenttextid;
        this.consentversionid = consentversion;
        this.cdRef.detectChanges();
      });

    this.globalText = PREQUAL_JSON;
    this.errorMessage =
      PREQUAL_JSON?.identification?.formsFields?.ssn.errors.ide4;
    const { errors: { ide1 = '', ide2 = '', ide3 = '' } = {} } = (PREQUAL_JSON
      ?.identification?.formsFields?.ssn || {}) as SsnFormFieldErrorLabels;
    this.formFieldErrorsMap = {
      isNotEmpty: ide1,
      isOfLength: ide2,
      isMatchingPattern: ide3,
    };

    this.agreementControl.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.adobeScriptLoaded && adobeAnalytics.emitFormStart('agreement');
        this.setConsent.emit(value);
      });
  }

  initForm(): void {
    this.retrieveData();
    const identityData = this.prequalService.getIdentityData();
    if (identityData && identityData.ssn) {
      this.agreementControl.setValue(false);
    }

    this.formIsIncomplete$ = this.identificationForm.statusChanges.pipe(
      map((status) => {
        const { ssn, agreement_consent } = this.identificationForm.controls;
        return !(
          status === 'VALID' &&
          ssn.status === 'VALID' &&
          agreement_consent.value === true
        );
      }),
      startWith(true)
    );
  }

  retrieveData() {
    const identityData = this.prequalService.getIdentityData();
    if (identityData && identityData.ssn) {
      this.identificationForm.markAllAsTouched();
      this.identificationForm.setValue(identityData);
    }
  }

  fieldValidator(): ValidatorFn {
    return (controlToValidate: AbstractControl): ValidationErrors | null => {
      const controlName = Object.entries(
        controlToValidate.parent?.controls ?? {}
      ).find(([_, control]) => control === controlToValidate)?.[0];
      if (controlName) {
        const { errors } = sharedFormValidator(
          'identificationFormRules',
          controlName as keyof typeof identificationFormRules,
          controlToValidate.value
        );
        if (errors && errors.length) {
          const ruleName = errors[0].rule;
          return { [ruleName]: this.formFieldErrorsMap[ruleName] };
        } else {
          return null;
        }
      }
      return null;
    };
  }

  get f() {
    return this.identificationForm.controls;
  }

  get ssnControl() {
    return this.identificationForm.controls.ssn;
  }

  get agreementControl() {
    return this.identificationForm.controls.agreement_consent;
  }

  disableSsnMessage() {
    this.ssnInvalidErrMessage = false;
  }

  resetConsentAgreement() {
    this.agreementControl.setValue(false);
  }
}
